<template>
  <div class="trame-page">
    <HeaderHome :user="this.$store.state.user.prenom"/>

    <div class="app-layout">

      <!-- ===== SIDEBAR ===== -->
      <aside class="sidebar">
        <div class="sidebar-header">
          <h2>Trames de formation</h2>
        </div>

        <div class="trame-list">
          <div v-if="filteredTrames.length === 0" class="trame-list-empty">Aucune trame trouvée</div>
          <div
            v-for="t in filteredTrames"
            :key="t.id"
            class="trame-item"
            :class="{ active: t.id === currentTrameId }"
            @click="selectTrame(t.id)"
          >
            <div class="trame-item-name">
              <span class="badge-type" :class="'badge-' + t.type.toLowerCase()">{{ t.type }}</span>
              {{ t.name }}
            </div>
            <div class="trame-item-meta">
              <span>{{ t.competences.length }} compétence{{ t.competences.length !== 1 ? 's' : '' }}</span>
              <span>·</span>
              <span>{{ formatDuree(getTotalDuree(t)) }}</span>
            </div>
          </div>
        </div>

      </aside>

      <!-- ===== EDITOR ===== -->
      <main class="editor">

        <!-- Empty state -->
        <div class="empty-state" v-if="!currentTrame">
          <div class="empty-icon">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
              <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8m-4-4v4"/>
            </svg>
          </div>
          <h3>Aucune trame sélectionnée</h3>
          <p>Sélectionnez une trame dans la liste ou créez-en une nouvelle</p>
          <button class="btn btn-green" @click="openNewTrameModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            Créer une trame
          </button>
        </div>

        <!-- Editor content -->
        <template v-else>

          <!-- Header -->
          <div class="editor-header">
            <div>
              <div class="editor-title">{{ currentTrame.name || 'Nouvelle trame' }}</div>
              <div class="editor-sub">Type de boîte : <strong>{{ currentTrame.type === 'BA' ? 'Boîte automatique (BA)' : 'Boîte manuelle (BM)' }}</strong></div>
            </div>
            <div class="editor-actions">
              <button class="btn btn-primary btn-sm" @click="saveTrame">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                </svg>
                Sauvegarder
              </button>
            </div>
          </div>

          <!-- Stats -->
          <div class="trame-stats">
            <div class="stat-pill"><strong>{{ currentTrame.competences.length }}</strong> compétence{{ currentTrame.competences.length !== 1 ? 's' : '' }}</div>
            <div class="stat-pill"><strong>{{ getTotalObjectifs(currentTrame) }}</strong> objectif{{ getTotalObjectifs(currentTrame) !== 1 ? 's' : '' }}</div>
            <div class="stat-pill"><strong>{{ getTotalSousObjectifs(currentTrame) }}</strong> sous-objectif{{ getTotalSousObjectifs(currentTrame) !== 1 ? 's' : '' }}</div>
            <div class="stat-pill"><strong>{{ formatDuree(getTotalDuree(currentTrame)) }}</strong> au total</div>
          </div>

          <!-- Infos générales -->
          <div class="form-card">
            <div class="form-card-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/>
              </svg>
              Informations générales
            </div>
            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Nom de la trame</label>
                <input type="text" class="form-input" v-model="currentTrame.name" placeholder="Ex : Formation B standard…">
              </div>
              <div class="form-group">
                <label class="form-label">Type de boîte</label>
                <select class="form-select" v-model="currentTrame.type">
                  <option value="BA">BA — Boîte automatique</option>
                  <option value="BM">BM — Boîte manuelle</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Compétences -->
          <div class="competences-section">
            <div class="section-header">
              <div class="section-title">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Compétences
                <span class="section-count">{{ currentTrame.competences.length }}</span>
              </div>
              <button class="btn btn-xs btn-primary" @click="addCompetence">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Ajouter
              </button>
            </div>

            <div v-if="currentTrame.competences.length === 0" class="comp-empty-msg">
              Aucune compétence — cliquez sur "Ajouter" pour en créer une
            </div>

            <!-- Competence card -->
            <div
              v-for="comp in currentTrame.competences"
              :key="comp.id"
              class="competence-card comp-anim"
              :class="{ open: openComps[comp.id] }"
            >
              <div class="competence-header" @click="toggleComp(comp.id)">
                <div class="drag-handle">
                  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor">
                    <circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>
                    <circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/>
                    <circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/>
                  </svg>
                </div>
                <div class="comp-num" :style="compNumStyle(comp)">{{ comp.num }}</div>
                <input
                  class="comp-label-input"
                  :value="comp.label"
                  placeholder="Nom de la compétence"
                  @click.stop
                  @input.stop
                  @change.stop="comp.label = $event.target.value"
                >
                <div class="color-picker-wrap" @click.stop>
                  <button class="color-swatch" :style="{ background: comp.color }" title="Changer la couleur">
                    <input type="color" :value="comp.color" @change="updateCompColor(comp, $event.target.value)">
                  </button>
                </div>
                <span class="comp-obj-count">{{ comp.objectifs.length }} obj.</span>
                <div class="comp-actions">
                  <button class="btn-icon danger" title="Supprimer" @click.stop="deleteCompetence(comp.id)">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
                <div class="comp-chevron">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </div>
              </div>

              <div class="competence-body">
                <div class="objectifs-list">

                  <!-- Objectif -->
                  <div
                    v-for="obj in comp.objectifs"
                    :key="obj.id"
                    class="objectif-item"
                    :class="{ open: openObjs[obj.id] }"
                  >
                    <div class="objectif-header" @click="toggleObj(obj.id)">
                      <div class="drag-handle">
                        <svg width="8" height="14" viewBox="0 0 10 16" fill="currentColor">
                          <circle cx="2" cy="2" r="1.5"/><circle cx="8" cy="2" r="1.5"/>
                          <circle cx="2" cy="8" r="1.5"/><circle cx="8" cy="8" r="1.5"/>
                          <circle cx="2" cy="14" r="1.5"/><circle cx="8" cy="14" r="1.5"/>
                        </svg>
                      </div>
                      <div class="obj-num">{{ obj.num }}</div>
                      <input
                        class="obj-label-input"
                        :value="obj.label"
                        placeholder="Nom de l'objectif"
                        @click.stop
                        @input.stop
                        @change.stop="obj.label = $event.target.value"
                      >
                      <span class="obj-sub-count">{{ obj.sousObjectifs.length }} s.obj · {{ getSousObjDuree(obj) }} min</span>
                      <button class="btn-icon danger" title="Supprimer" @click.stop="deleteObjectif(comp.id, obj.id)">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                          <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                      <div class="obj-chevron">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </div>
                    </div>

                    <div class="objectif-body">
                      <div class="sous-objectifs-list">
                        <!-- Sous-objectif -->
                        <div
                          v-for="so in obj.sousObjectifs"
                          :key="so.id"
                          class="sous-objectif-row"
                        >
                          <div class="sous-obj-num">{{ so.num }}</div>
                          <input
                            class="sous-obj-input"
                            :value="so.label"
                            placeholder="Libellé du sous-objectif"
                            @change="so.label = $event.target.value"
                          >
                          <div class="sous-obj-duration">
                            <input
                              type="number"
                              class="duration-input"
                              :value="so.duree"
                              min="0" max="999"
                              placeholder="—"
                              @change="so.duree = parseInt($event.target.value) || 0"
                            >
                            <span class="duration-unit">min</span>
                          </div>
                          <button class="btn-icon danger" title="Supprimer" @click="deleteSousObj(comp.id, obj.id, so.id)">
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                      <div class="add-row">
                        <button class="add-btn-inline" @click="addSousObjectif(comp.id, obj.id)">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                          </svg>
                          Ajouter un sous-objectif
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
                <div class="add-row obj-add-row">
                  <button class="add-btn-inline" @click="addObjectif(comp.id)">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Ajouter un objectif
                  </button>
                </div>
              </div>
            </div>

          </div>
        </template>
      </main>
    </div>

    <!-- ===== MODAL NOUVELLE TRAME ===== -->
    <div class="modal-bg" :class="{ open: showNewTrameModal }" @click.self="closeModal">
      <div class="modal">
        <div class="modal-title">Nouvelle trame de formation</div>
        <div class="form-group" style="margin-bottom:14px;">
          <label class="form-label">Nom</label>
          <input
            ref="modalNameInput"
            type="text"
            class="form-input"
            v-model="modalName"
            placeholder="Ex : Formation B manuelle standard…"
            @keydown.enter="confirmNewTrame"
          >
        </div>
        <div class="form-group">
          <label class="form-label">Type de boîte</label>
          <select class="form-select" v-model="modalType">
            <option value="BA">BA — Boîte automatique</option>
            <option value="BM">BM — Boîte manuelle</option>
          </select>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeModal">Annuler</button>
          <button class="btn btn-green" @click="confirmNewTrame">Créer</button>
        </div>
      </div>
    </div>

    <!-- ===== TOASTS ===== -->
    <div class="toast-wrap">
      <div v-for="t in toasts" :key="t.id" class="toast" :class="t.type">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        {{ t.msg }}
      </div>
    </div>
  </div>
</template>

<script>
import HeaderHome from "../../components/home/HeaderHome.vue";

export default {
  name: "Trame",
  components: { HeaderHome },

  data() {
    return {
      trames: [],
      currentTrameId: null,
      searchFilter: "",
      showNewTrameModal: false,
      modalName: "",
      modalType: "BM",
      openComps: {},
      openObjs: {},
      toasts: [],
    };
  },

  computed: {
    currentTrame() {
      return this.trames.find((t) => t.id === this.currentTrameId) || null;
    },
    filteredTrames() {
      const f = this.searchFilter.toLowerCase();
      return this.trames.filter((t) => t.name.toLowerCase().includes(f));
    },
  },

  mounted() {
    window.api
      .get("/trames/all", {
        headers: { Authorization: "Bearer " + this.$store.state.token },
      })
      .then((response) => {
        this.trames = response.data.map((t) => ({
          id: t.id,
          name: t.nom,
          type: t.typeBoite?.label || "",
          competences: t.competences.map((c) => ({
            id: c.id,
            num: c.numero,
            label: c.label,
            color: c.color,
            objectifs: c.objectifs.map((o) => ({
              id: o.id,
              num: o.numero,
              label: o.label,
              sousObjectifs: o.sousObjectifs.map((so) => ({
                id: so.id,
                num: so.numero,
                label: so.label,
                duree: so.duree,
              })),
            })),
          })),
        }));
        if (this.trames.length > 0) this.selectTrame(this.trames[0].id);
      })
      .catch((error) => { console.log(error); });
  },

  methods: {
    // ===== UTILS =====
    uid() {
      return Math.random().toString(36).slice(2, 10);
    },
    getTotalDuree(t) {
      return t.competences.reduce(
        (s, c) => s + c.objectifs.reduce(
          (s2, o) => s2 + o.sousObjectifs.reduce((s3, so) => s3 + (parseInt(so.duree) || 0), 0), 0), 0);
    },
    getTotalObjectifs(t) {
      return t.competences.reduce((s, c) => s + c.objectifs.length, 0);
    },
    getTotalSousObjectifs(t) {
      return t.competences.reduce(
        (s, c) => s + c.objectifs.reduce((s2, o) => s2 + o.sousObjectifs.length, 0), 0);
    },
    getSousObjDuree(obj) {
      return obj.sousObjectifs.reduce((s, so) => s + (parseInt(so.duree) || 0), 0);
    },
    formatDuree(minutes) {
      const h = Math.floor(minutes / 60);
      const m = minutes % 60;
      if (h === 0) return `${m}min`;
      return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`;
    },
    renumberArray(arr) {
      arr.forEach((x, i) => { x.num = i + 1; });
    },
    getContrastColor(hex) {
      try {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55 ? "#1a1f2e" : "#ffffff";
      } catch { return "#1a1f2e"; }
    },
    compNumStyle(comp) {
      return { background: comp.color, borderColor: comp.color, color: this.getContrastColor(comp.color) };
    },

    // ===== SIDEBAR =====
    selectTrame(id) {
      this.currentTrameId = id;
    },

    // ===== TOGGLES =====
    toggleComp(id) {
      this.openComps[id] = !this.openComps[id];
    },
    toggleObj(id) {
      this.openObjs[id] = !this.openObjs[id];
    },

    // ===== COMPETENCES =====
    addCompetence() {
      const t = this.currentTrame; if (!t) return;
      const num = t.competences.length + 1;
      const colors = ["#A6F5C5","#A6C8F5","#F5E6A6","#F5A6C8","#C8A6F5","#F5C8A6","#A6F5F5"];
      const id = this.uid();
      t.competences.push({ id, num, label: `Compétence ${num}`, color: colors[(num - 1) % colors.length], objectifs: [] });
      this.$nextTick(() => { this.openComps[id] = true; });
    },
    deleteCompetence(cid) {
      const t = this.currentTrame; if (!t) return;
      t.competences = t.competences.filter((c) => c.id !== cid);
      this.renumberArray(t.competences);
      this.toast("Compétence supprimée");
    },
    updateCompColor(comp, val) {
      comp.color = val;
    },

    // ===== OBJECTIFS =====
    addObjectif(cid) {
      const t = this.currentTrame; if (!t) return;
      const c = t.competences.find((x) => x.id === cid); if (!c) return;
      const num = c.objectifs.length + 1;
      const id = this.uid();
      c.objectifs.push({ id, num, label: `Objectif ${num}`, sousObjectifs: [] });
      this.$nextTick(() => {
        this.openComps[cid] = true;
        this.openObjs[id] = true;
      });
    },
    deleteObjectif(cid, oid) {
      const t = this.currentTrame; if (!t) return;
      const c = t.competences.find((x) => x.id === cid); if (!c) return;
      c.objectifs = c.objectifs.filter((o) => o.id !== oid);
      this.renumberArray(c.objectifs);
      this.$nextTick(() => { this.openComps[cid] = true; });
      this.toast("Objectif supprimé");
    },

    // ===== SOUS-OBJECTIFS =====
    addSousObjectif(cid, oid) {
      const t = this.currentTrame; if (!t) return;
      const c = t.competences.find((x) => x.id === cid); if (!c) return;
      const o = c.objectifs.find((x) => x.id === oid); if (!o) return;
      const num = o.sousObjectifs.length + 1;
      o.sousObjectifs.push({ id: this.uid(), num, label: `Sous-objectif ${num}`, duree: 0 });
      this.$nextTick(() => {
        this.openComps[cid] = true;
        this.openObjs[oid] = true;
      });
    },
    deleteSousObj(cid, oid, soid) {
      const t = this.currentTrame; if (!t) return;
      const c = t.competences.find((x) => x.id === cid); if (!c) return;
      const o = c.objectifs.find((x) => x.id === oid); if (!o) return;
      o.sousObjectifs = o.sousObjectifs.filter((so) => so.id !== soid);
      this.renumberArray(o.sousObjectifs);
      this.$nextTick(() => {
        this.openComps[cid] = true;
        this.openObjs[oid] = true;
      });
      this.toast("Sous-objectif supprimé");
    },

    // ===== SAVE / DELETE TRAME =====
    dbId(id) {
      // Retourne l'id si c'est un entier DB, null si c'est un id local (string uid)
      return Number.isInteger(id) ? id : null;
    },
    saveTrame() {
      const t = this.currentTrame; if (!t) return;
      const payload = {
        nom: t.name,
        competences: t.competences.map((c) => ({
          id: this.dbId(c.id),
          numero: c.num,
          label: c.label,
          color: c.color,
          objectifs: c.objectifs.map((o) => ({
            id: this.dbId(o.id),
            numero: o.num,
            label: o.label,
            sousObjectifs: o.sousObjectifs.map((so) => ({
              id: this.dbId(so.id),
              numero: so.num,
              label: so.label,
              duree: so.duree,
            })),
          })),
        })),
      };
      window.api
        .put("/trame/update/" + t.id, payload, {
          headers: { Authorization: "Bearer " + this.$store.state.token },
        })
        .then(() => { this.toast("Trame sauvegardée", "success"); })
        .catch((error) => { console.log(error); this.toast("Erreur lors de la sauvegarde", "error"); });
    },
    deleteTrame() {
      if (!this.currentTrameId) return;
      if (!confirm("Supprimer cette trame définitivement ?")) return;
      this.trames = this.trames.filter((t) => t.id !== this.currentTrameId);
      this.currentTrameId = null;
      this.toast("Trame supprimée");
    },

    // ===== MODAL =====
    openNewTrameModal() {
      this.modalName = "";
      this.modalType = "BM";
      this.showNewTrameModal = true;
      this.$nextTick(() => { this.$refs.modalNameInput?.focus(); });
    },
    closeModal() {
      this.showNewTrameModal = false;
    },
    confirmNewTrame() {
      const name = this.modalName.trim();
      if (!name) { this.$refs.modalNameInput?.focus(); return; }
      const t = { id: this.uid(), name, type: this.modalType, competences: [] };
      this.trames.unshift(t);
      this.closeModal();
      this.selectTrame(t.id);
      this.toast("Trame créée", "success");
    },

    // ===== EXPORT =====
    exportJSON() {
      const data = JSON.stringify(this.trames, null, 2);
      const blob = new Blob([data], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "trames_formation.json";
      a.click(); URL.revokeObjectURL(url);
      this.toast("Export JSON téléchargé", "success");
    },

    // ===== TOAST =====
    toast(msg, type = "") {
      const id = this.uid();
      this.toasts.push({ id, msg, type });
      setTimeout(() => {
        this.toasts = this.toasts.filter((t) => t.id !== id);
      }, 2800);
    },
  },
};
</script>

<style scoped>
.trame-page {
  background: #f4f6f9;
  color: #1a1f2e;
  min-height: 100vh;
}

/* ===== LAYOUT ===== */
.app-layout {
  display: grid;
  grid-template-columns: 320px 1fr;
  min-height: calc(100vh - 60px);
}

/* ===== SIDEBAR ===== */
.sidebar {
  background: #fff;
  border-right: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 60px);
  position: sticky;
  top: 60px;
  overflow: hidden;
}
.sidebar-header {
  padding: 20px 20px 16px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.sidebar-header h2 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 0;
}

.trame-list {
  overflow-y: auto;
  flex: 1;
  padding: 8px;
}
.trame-list::-webkit-scrollbar { width: 4px; }
.trame-list::-webkit-scrollbar-track { background: transparent; }
.trame-list::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
.trame-list-empty {
  padding: 24px; text-align: center; font-size: 13px; color: #94a3b8;
}

.trame-item {
  padding: 12px 14px;
  border-radius: 10px;
  cursor: pointer;
  transition: background .12s;
  margin-bottom: 2px;
  border: 1px solid transparent;
}
.trame-item:hover { background: #f4f6f9; }
.trame-item.active { background: #eff6ff; border-color: #bfdbfe; }
.trame-item-name {
  font-size: 13px; font-weight: 600;
  color: #1a1f2e;
  margin-bottom: 4px;
  display: flex; align-items: center; gap: 6px;
}
.trame-item.active .trame-item-name { color: #2563eb; }
.trame-item-meta {
  font-size: 11px; color: #94a3b8;
  display: flex; align-items: center; gap: 8px;
}

.badge-type {
  display: inline-flex; align-items: center;
  padding: 2px 8px; border-radius: 20px;
  font-size: 10px; font-weight: 700; letter-spacing: .02em;
}
.badge-ba { background: #e8f7f1; color: #065f46; }
.badge-bm { background: #fee2e2; color: #b91c1c; }

.sidebar-footer {
  padding: 12px;
  border-top: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.sidebar-btn { width: 100%; justify-content: center; }

/* ===== EDITOR ===== */
.editor {
  padding: 28px 32px;
  overflow-y: auto;
}
.editor::-webkit-scrollbar { width: 5px; }
.editor::-webkit-scrollbar-track { background: transparent; }
.editor::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }

/* ===== EDITOR HEADER ===== */
.editor-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}
.editor-title {
  font-size: 26px; font-weight: 700;
  color: #1a1f2e; letter-spacing: -.4px;
  text-align: left;
}
.editor-sub { font-size: 13px; color: #64748b; margin-top: 2px; text-align: left; }
.editor-actions { display: flex; gap: 8px; }

/* ===== STATS ===== */
.trame-stats { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
.stat-pill {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 20px;
  font-size: 12px; font-weight: 600;
  background: #f4f6f9; border: 1px solid #e2e8f0; color: #64748b;
}
.stat-pill strong { color: #1a1f2e; }

/* ===== FORM CARD ===== */
.form-card {
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 14px; padding: 24px;
  margin-bottom: 20px; box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04);
}
.form-card-title {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .08em; color: #94a3b8;
  margin-bottom: 16px; display: flex; align-items: center; gap: 8px;
}
.form-row {
  display: grid; grid-template-columns: 1fr 200px;
  gap: 16px; align-items: end;
}
.form-group { display: flex; flex-direction: column; gap: 5px; }
.form-label { font-size: 12px; font-weight: 600; color: #64748b; }
.form-input {
  padding: 9px 12px; border: 1px solid #e2e8f0; border-radius: 9px;
  font-size: 13px;
  background: #fff; color: #1a1f2e; outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.form-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }
.form-select {
  padding: 9px 32px 9px 12px; border: 1px solid #e2e8f0; border-radius: 9px;
  font-size: 13px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat right 10px center;
  -webkit-appearance: none; color: #1a1f2e;
  cursor: pointer; outline: none; transition: border-color .15s;
}
.form-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }

/* ===== COMPETENCES SECTION ===== */
.competences-section {
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 1px 3px rgba(0,0,0,.06), 0 1px 2px rgba(0,0,0,.04); margin-bottom: 20px;
}
.section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; background: #f4f6f9; border-bottom: 1px solid #e2e8f0;
}
.section-title {
  font-size: 12px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .08em; color: #64748b;
  display: flex; align-items: center; gap: 8px;
}
.section-count {
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px; border-radius: 50%;
  background: #e2e8f0; font-size: 11px; font-weight: 700; color: #64748b;
}
.comp-empty-msg {
  padding: 24px; text-align: center; font-size: 13px; color: #94a3b8;
}

/* ===== COMPETENCE CARD ===== */
.competence-card { border-bottom: 1px solid #e2e8f0; transition: background .1s; }
.competence-card:last-child { border-bottom: none; }

.competence-header {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; cursor: pointer; user-select: none; transition: background .1s;
}
.competence-header:hover { background: #fafbfc; }

.comp-num {
  width: 26px; height: 26px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 11px; font-weight: 700; flex-shrink: 0; border: 2px solid;
}
.comp-chevron {
  width: 18px; height: 18px;
  display: flex; align-items: center; justify-content: center;
  color: #94a3b8; transition: transform .2s; flex-shrink: 0; margin-left: auto;
}
.competence-card.open .comp-chevron { transform: rotate(180deg); }
.comp-label-input {
  flex: 1; padding: 5px 8px; border: 1px solid #e2e8f0; border-radius: 7px;
  font-size: 13px; font-weight: 600;
  color: #1a1f2e; outline: none; background: #fff; transition: border-color .15s;
}
.comp-label-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }
.comp-obj-count { font-size: 11px; color: #94a3b8; white-space: nowrap; }

.color-picker-wrap { position: relative; display: flex; align-items: center; gap: 6px; }
.color-swatch {
  width: 28px; height: 28px; border-radius: 8px; border: 2px solid #e2e8f0;
  cursor: pointer; transition: border-color .15s; flex-shrink: 0; overflow: hidden; padding: 0; background: none;
}
.color-swatch:hover { border-color: #94a3b8; }
.color-swatch input[type="color"] {
  width: 40px; height: 40px; border: none; outline: none;
  cursor: pointer; background: none; margin: -6px 0 0 -6px;
}

.comp-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

/* ===== COMPETENCE BODY ===== */
.competence-body {
  display: none; padding: 0 20px 16px 48px;
  border-top: 1px solid #e2e8f0; background: #fafbfc;
}
.competence-card.open .competence-body { display: block; }

.objectifs-list { padding-top: 14px; }

/* ===== OBJECTIF ITEM ===== */
.objectif-item {
  margin-bottom: 6px; border: 1px solid #e2e8f0;
  border-radius: 10px; background: #fff; overflow: hidden;
}
.objectif-header {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; cursor: pointer; user-select: none; transition: background .1s;
}
.objectif-header:hover { background: #fafbfc; }
.obj-num {
  width: 22px; height: 22px; border-radius: 6px; background: #f4f6f9;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: #64748b;
  flex-shrink: 0; border: 1px solid #e2e8f0;
}
.obj-label-input {
  flex: 1; padding: 4px 8px; border: 1px solid #e2e8f0; border-radius: 6px;
  font-size: 13px; font-weight: 500;
  color: #1a1f2e; outline: none; background: #fff; transition: border-color .15s;
}
.obj-label-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); }
.obj-chevron {
  width: 16px; height: 16px;
  display: flex; align-items: center; justify-content: center;
  color: #94a3b8; transition: transform .2s; flex-shrink: 0; margin-left: auto;
}
.objectif-item.open .obj-chevron { transform: rotate(180deg); }
.obj-sub-count {
  font-size: 11px; color: #94a3b8; background: #f4f6f9;
  padding: 2px 8px; border-radius: 20px; border: 1px solid #e2e8f0;
  white-space: nowrap; flex-shrink: 0;
}

/* ===== OBJECTIF BODY ===== */
.objectif-body {
  display: none; padding: 0 14px 12px 36px;
  border-top: 1px solid #f1f5f9; background: #f4f6f9;
}
.objectif-item.open .objectif-body { display: block; }

.sous-objectifs-list { padding-top: 10px; }

/* ===== SOUS-OBJECTIF ROW ===== */
.sous-objectif-row {
  display: grid;
  grid-template-columns: 22px 1fr 110px 28px;
  gap: 8px; align-items: center;
  padding: 7px 10px; border-radius: 8px;
  background: #fff; border: 1px solid #e2e8f0;
  margin-bottom: 5px; transition: border-color .15s;
}
.sous-objectif-row:hover { border-color: #94a3b8; }
.sous-obj-num {
  width: 22px; height: 22px; border-radius: 5px; background: #e2e8f0;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 700; color: #94a3b8;
}
.sous-obj-input {
  padding: 4px 8px; border: 1px solid #e2e8f0; border-radius: 6px;
  font-size: 12px; color: #1a1f2e;
  outline: none; background: #f4f6f9; transition: border-color .15s, background .15s;
}
.sous-obj-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); background: #fff; }
.sous-obj-duration { display: flex; align-items: center; gap: 4px; }
.duration-input {
  width: 58px; padding: 4px 6px; border: 1px solid #e2e8f0; border-radius: 6px;
  font-size: 12px; font-weight: 600;
  color: #1a1f2e; outline: none; text-align: right;
  background: #f4f6f9; transition: border-color .15s;
}
.duration-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,.08); background: #fff; }
.duration-unit { font-size: 11px; color: #94a3b8; font-weight: 500; white-space: nowrap; }

/* ===== ADD ROWS ===== */
.add-row { display: flex; align-items: center; gap: 8px; padding: 8px 0 0; }
.obj-add-row { padding: 8px 20px 0 48px; padding-bottom: 0; }
.add-btn-inline {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 10px; border-radius: 7px; font-size: 12px; font-weight: 600;
  border: 1px dashed #e2e8f0; background: none; color: #94a3b8;
  cursor: pointer; transition: all .12s;
}
.add-btn-inline:hover { border-color: #3b82f6; color: #3b82f6; background: #eff6ff; }

/* ===== DRAG HANDLE ===== */
.drag-handle {
  display: flex; align-items: center; justify-content: center;
  width: 16px; height: 24px; color: #94a3b8;
  cursor: grab; flex-shrink: 0; opacity: 0; transition: opacity .12s;
}
.competence-header:hover .drag-handle,
.objectif-header:hover .drag-handle { opacity: 1; }
.drag-handle:active { cursor: grabbing; }

/* ===== BUTTONS ===== */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 16px; border-radius: 9px;
  font-size: 13px; font-weight: 600;
  cursor: pointer; border: none; transition: all .15s; white-space: nowrap;
}
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover { background: #2563eb; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(59,130,246,.3); }
.btn-green { background: #1a9e6e; color: #fff; }
.btn-green:hover { background: #168a5f; transform: translateY(-1px); }
.btn-secondary { background: #f4f6f9; color: #64748b; border: 1px solid #e2e8f0; }
.btn-secondary:hover { background: #fff; color: #1a1f2e; border-color: #94a3b8; }
.btn-sm { padding: 6px 12px; font-size: 12px; border-radius: 8px; }
.btn-xs { padding: 4px 10px; font-size: 11px; border-radius: 7px; gap: 4px; }
.btn-icon {
  width: 28px; height: 28px; padding: 0; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  background: #f4f6f9; border: 1px solid #e2e8f0;
  cursor: pointer; color: #64748b; transition: all .12s;
}
.btn-icon:hover { background: #fff; color: #1a1f2e; border-color: #94a3b8; }
.btn-icon.danger:hover { background: #fee2e2; color: #ef4444; border-color: #fca5a5; }

/* ===== EMPTY STATE ===== */
.empty-state {
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 80px 32px; text-align: center;
}
.empty-icon {
  width: 64px; height: 64px; border-radius: 16px;
  background: #f4f6f9; border: 2px dashed #e2e8f0;
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 16px; color: #94a3b8;
}
.empty-state h3 { font-size: 20px; font-weight: 600; color: #1a1f2e; margin-bottom: 6px; }
.empty-state p { font-size: 13px; color: #64748b; margin-bottom: 24px; }

/* ===== MODAL ===== */
.modal-bg {
  display: none; position: fixed; inset: 0;
  background: rgba(0,0,0,.3); z-index: 500;
  align-items: center; justify-content: center; backdrop-filter: blur(2px);
}
.modal-bg.open { display: flex; }
.modal {
  background: #fff; border-radius: 14px; padding: 28px;
  width: 480px; max-width: calc(100vw - 32px);
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04); animation: fadeModal .2s ease;
}
@keyframes fadeModal { from { opacity: 0; transform: scale(.96); } to { opacity: 1; transform: none; } }
.modal-title { font-size: 20px; font-weight: 600; margin-bottom: 20px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; }

/* ===== TOAST ===== */
.toast-wrap {
  position: fixed; bottom: 24px; right: 24px; z-index: 1000;
  display: flex; flex-direction: column; gap: 8px; pointer-events: none;
}
.toast {
  background: #1a1f2e; color: #fff;
  padding: 10px 16px; border-radius: 10px; font-size: 13px; font-weight: 500;
  box-shadow: 0 4px 16px rgba(0,0,0,.08), 0 2px 6px rgba(0,0,0,.04);
  display: flex; align-items: center; gap: 8px;
  animation: slideUp .25s ease; pointer-events: all;
}
.toast.success { background: #1a9e6e; }
.toast.error   { background: #ef4444; }
@keyframes slideUp { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

/* ===== ANIMATIONS ===== */
@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
.comp-anim { animation: fadeIn .2s ease; }

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .app-layout { grid-template-columns: 1fr; }
  .sidebar { height: auto; position: static; max-height: 280px; }
  .editor { padding: 20px; }
  .form-row { grid-template-columns: 1fr; }
}
</style>
